import { track } from '../../lib/analytics.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { startBooking } from '../../lib/booking-api.js';
import { markBookingHandoff } from '../../lib/booking-handoff.js';
import { CONTACT_EMAIL } from '../../lib/site-config.js';
import { Eyebrow } from '../Section.jsx';
import { useModalFocus } from '../../lib/use-modal-focus.js';

// Commit-first flow (owner call, 2026-07-28: "simplify the decision, get them
// to commit"): the page itself asks for nothing but dates. Guest count and
// contact details live in a focused overlay that opens on Book direct; the
// OwnerRez payment page still takes the card. The overlay is a hard
// requirement, not cosmetics — the booking function needs a name and email to
// create the guest + persisted quote before it can mint the payment URL.

function parseYmd(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const fmtDate = (str) =>
  parseYmd(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function quoteTotal(quote) {
  if (!quote) return null;
  const t = quote.total ?? quote.total_amount ?? quote.amount;
  if (t != null) return Number(t);
  if (Array.isArray(quote.charges)) {
    return quote.charges.reduce((a, c) => a + (Number(c.amount) || 0), 0);
  }
  return null;
}

const fmtUsd = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

// text-base: 16px stops iOS Safari zooming the page when a field takes focus.
const inputClass =
  'mt-1 w-full rounded-lg border border-navy-200 bg-cream-50 px-3 py-2 text-base text-navy-900';
const labelClass =
  'block text-xs font-medium uppercase tracking-wider text-navy-600';

export default function BookingForm({
  property,
  range,
  quote,
  quoteError = null,
  adults,
  childrenCount,
  onAdultsChange,
  onChildrenChange,
  showDiscountField = false,
  discountCode = '',
  discountInfo = null,
  onApplyDiscountCode
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [codeInput, setCodeInput] = useState(discountCode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const firstFieldRef = useRef(null);
  const panelRef = useRef(null);

  // A failed refetch leaves the previous `quote` in place (Stay.jsx sets
  // quoteError without clearing it), so every gate below has to read the error
  // too. Otherwise raising the guest count past capacity kept the old total on
  // screen with "Continue to secure payment" still live.
  const hasDates = Boolean(range.start && range.end);
  const hasQuote = Boolean(hasDates && quote && !quoteError);
  const canBook = hasQuote && firstName && email;
  const nights = hasDates
    ? Math.round((parseYmd(range.end) - parseYmd(range.start)) / 86400000)
    : 0;
  const total = hasQuote ? quoteTotal(quote) : null;

  // OwnerRez caps adults, children and the total separately, and it refuses
  // the quote when any of the three is exceeded. Both selects used to run to
  // `sleeps`, so together they offered fifteen people to a house that takes
  // eight. Offering only what can be booked is the fix; the server is still
  // the authority, and its sentence is what the guest sees if a combination
  // slips through.
  const maxAdults = Math.min(property.maxAdults ?? property.sleeps, property.sleeps);
  const maxChildren = Math.min(property.maxChildren ?? property.sleeps - 1, property.sleeps - 1);
  const adultOptions = Array.from(
    { length: Math.max(1, Math.min(maxAdults, property.sleeps - childrenCount)) },
    (_, i) => i + 1
  );
  const childOptions = Array.from(
    { length: Math.max(1, Math.min(maxChildren, property.sleeps - adults) + 1) },
    (_, i) => i
  );

  // Overlay plumbing, shared with the photo lightbox: Escape closes, Tab stays
  // inside the dialog, the page behind doesn't scroll, focus lands on the
  // first field so keyboard users start typing immediately, and closing hands
  // focus back to the Book direct button that opened it. Before this, four
  // tabs put a keyboard user behind the backdrop in content they could not see
  // or scroll.
  const closeOverlay = useCallback(() => setDetailsOpen(false), []);
  useModalFocus(detailsOpen, panelRef, {
    onEscape: closeOverlay,
    initialFocusRef: firstFieldRef
  });

  // Clearing the dates closes the overlay; there is nothing left to book. A
  // refused quote keeps it open, because the guest is standing in it and the
  // guest-count selects that caused the refusal are inside it. They see the
  // same sentence the card behind the backdrop shows, and the submit button is
  // already disabled by `canBook`.
  useEffect(() => {
    if (!hasDates) setDetailsOpen(false);
  }, [hasDates]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { paymentUrl } = await startBooking({
        arrival: range.start,
        departure: range.end,
        adults,
        children: childrenCount,
        guestFirstName: firstName,
        guestLastName: lastName,
        guestEmail: email,
        guestPhone: phone,
        propertySlug: property.slug,
        discountCode: discountCode || undefined
      });
      if (!paymentUrl) throw new Error('No payment link returned. Please try again.');
      // Last step we can see: the payment page is on OwnerRez's domain. Leave a
      // marker behind so the confirmation page, which OwnerRez returns to with
      // no session id, can tell this tab apart from a cold visit.
      markBookingHandoff(property.slug);
      track('checkout_started', { nights, slug: property.slug });
      window.location.href = paymentUrl;
    } catch (err) {
      track('checkout_failed', { nights, reason: err.message, slug: property.slug });
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="space-y-4">
        {/* Hidden friends-and-family block — only rendered once the Stay page
            reveal fires (typed keyword, five taps on the title, or a ?code=
            link). Deliberately no visible "have a code?" affordance. */}
        {showDiscountField && (
          <div className="rounded-lg border border-dashed border-cream-400 bg-cream-100/60 p-3">
            <span className={labelClass}>Discount code</span>
            <div className="mt-1 flex gap-2">
              <input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="CODE"
                className="w-full rounded-lg border border-navy-200 bg-cream-50 px-3 py-2 text-navy-900"
              />
              <button
                type="button"
                onClick={() => onApplyDiscountCode?.(codeInput.trim())}
                className="shrink-0 rounded-lg border border-navy-300 px-3 py-2 text-sm font-medium text-navy-800 hover:bg-cream-200"
              >
                Apply
              </button>
            </div>
            {hasQuote && discountCode && discountInfo && (
              discountInfo.applied ? (
                <p className="mt-2 text-xs font-medium text-marsh-700">
                  Code {discountInfo.code} applied. You're saving ${Number(discountInfo.amountOff).toFixed(2)}.
                </p>
              ) : (
                <p className="mt-2 text-xs text-charlestonpink-600">
                  {discountInfo.reason || "That code couldn't be applied."}
                </p>
              )
            )}
            {!hasQuote && discountCode && (
              <p className="mt-2 text-xs text-navy-500">
                Pick your dates and the discount will apply to the quote.
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          disabled={!hasQuote}
          onClick={() => {
            setDetailsOpen(true);
            track('book_direct_click', { nights, slug: property.slug });
          }}
          className={
            hasQuote
              ? 'w-full rounded-lg bg-navy-900 px-4 py-3 font-medium text-cream-50 transition-shadow hover:bg-navy-800 hover:ring-2 hover:ring-cream-500'
              : 'w-full cursor-not-allowed rounded-lg border border-navy-200 bg-cream-50 px-4 py-3 font-medium text-navy-500'
          }
        >
          Book direct
        </button>
        {/* With a refused quote the card directly above already says why, so
            this line steps back rather than telling a guest who has picked
            dates to pick dates. */}
        {!quoteError && (
          <p className="text-xs text-navy-500">
            {hasQuote
              ? 'Nothing is charged until the secure payment page.'
              : 'Select check-in and check-out dates above to see your price.'}
          </p>
        )}

        <p className="text-xs text-navy-500">
          Questions about the house?{' '}
          {/* -my-2 py-2: tap padding the negative margins hand back, so the
              link grows and the line it sits in does not. */}
          <a href={`mailto:${CONTACT_EMAIL}`} className="-my-2 inline-block py-2 text-navy-700 underline">
            Email us
          </a>{' '}
          and we'll reply within a few hours.
        </p>
      </div>

      {detailsOpen && hasDates && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-navy-950/50 p-4 backdrop-blur-sm sm:items-center"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeOverlay();
          }}
        >
          <form
            ref={panelRef}
            onSubmit={onSubmit}
            role="dialog"
            aria-modal="true"
            aria-label="Complete your booking"
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-navy-100 bg-cream-50 p-6 shadow-xl"
          >
            <Eyebrow text="Complete your booking" />
            <p className="font-display text-xl font-semibold text-navy-900">
              {fmtDate(range.start)} to {fmtDate(range.end)} · {nights} night{nights === 1 ? '' : 's'}
            </p>
            {quoteError ? (
              <p className="mt-1 text-sm text-charlestonpink-600">{quoteError}</p>
            ) : total != null ? (
              <p className="mt-1 text-sm text-navy-600">
                Total {fmtUsd(total)} · taxes included, no service fee
              </p>
            ) : null}

            <div className="mt-5 grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className={labelClass}>Adults</span>
                <select
                  value={adults}
                  onChange={(e) => onAdultsChange(Number(e.target.value))}
                  className={inputClass}
                >
                  {adultOptions.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                <span className={labelClass}>Children</span>
                <select
                  value={childrenCount}
                  onChange={(e) => onChildrenChange(Number(e.target.value))}
                  className={inputClass}
                >
                  {childOptions.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <p className="mt-2 text-xs text-navy-500">
              Sleeps {property.sleeps}, up to {maxAdults} adults.
            </p>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className={labelClass}>First name</span>
                <input
                  ref={firstFieldRef}
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="text-sm">
                <span className={labelClass}>Last name</span>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClass}
                />
              </label>
            </div>

            <label className="mt-3 block text-sm">
              <span className={labelClass}>Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </label>

            <label className="mt-3 block text-sm">
              <span className={labelClass}>Phone (optional)</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </label>

            {error && <div className="mt-3 text-sm text-charlestonpink-600">{error}</div>}

            <p className="mt-4 flex items-start gap-1.5 text-xs text-navy-700">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                Next stop is our booking system's secure payment page, showing this
                same itemized total. Nothing is charged until you confirm there.
              </span>
            </p>

            {/* Disabled: navy-600 on navy-100 is 6.49:1. Cream on navy-300 measured
                2.3:1, so the label washed out before the form was valid. */}
            <button
              type="submit"
              disabled={!canBook || submitting}
              className="mt-4 w-full rounded-lg bg-navy-900 px-4 py-3 font-medium text-cream-50 transition-shadow hover:bg-navy-800 hover:ring-2 hover:ring-cream-500 disabled:bg-navy-100 disabled:text-navy-600 disabled:ring-0"
            >
              {submitting ? 'Redirecting to payment…' : 'Continue to secure payment'}
            </button>
            <p className="mt-2 text-xs text-navy-500">
              Your dates are not held until checkout completes.
            </p>
            <button
              type="button"
              onClick={closeOverlay}
              className="mt-3 w-full text-center text-xs text-navy-600 underline"
            >
              Back to the calendar
            </button>
          </form>
        </div>
      )}
    </>
  );
}
